import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';

import { gaPush, QUESTION_PANEL_ACTIONS, QUESTION_PANEL_CATEGORY } from '@/configuration/setup/googleAnalytics';
import useRovingTabIndex from '@/hooks/useRovingTabIndex';
import { EFaqsCategoryIcons } from '@/store/faq/config';
import { EOrientation, SMALL_SCREEN_QUERY } from '@/utils/constants';

import CategoryControl from '../CategoryControl/CategoryControl';
import { CATEGORY_ORDER, EXAMPLE_QUESTION_PANEL_MESSAGES, EXAMPLE_QUESTION_PANEL_TEST_IDS } from '../constants';
import { CategoryRow } from '../ExampleQuestionPanel.styles';
import { ShortcutBarLabel, ShortcutBarRoot } from './ExampleQuestionsShortcutBar.styles';
import { TExampleQuestionsShortcutBarProps } from './types';

const ExampleQuestionsShortcutBar = ({
  hasDraft,
  isDisabled,
  isOnboarding,
  shouldRestoreFocus,
  focusCategory,
  onSelectCategory,
  onFocusRestored,
}: TExampleQuestionsShortcutBarProps) => {
  const intl = useIntl();

  // INFO: The shortcuts stack vertically below the breakpoint, so the arrow-key axis follows suit
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN_QUERY });
  const orientation = isSmallScreen ? EOrientation.VERTICAL : EOrientation.HORIZONTAL;

  /**
   * True while a Category Shortcut must not respond: the composer holds a draft that owns the send,
   * the slot is disabled outright, or onboarding has funnelled the user to Perform.
   */
  const isCategoryInoperable = useCallback(
    (category: EFaqsCategoryIcons) =>
      hasDraft || isDisabled || (isOnboarding && category !== EFaqsCategoryIcons.PERFORM),
    [hasDraft, isDisabled, isOnboarding],
  );

  /**
   * The single selection path for a Category Shortcut. Both the pointer and the keyboard route
   * through here so the tracking cannot count one input and miss the other.
   * @param category - The category the user selected.
   */
  const handleSelectCategory = useCallback(
    (category: EFaqsCategoryIcons) => {
      if (isCategoryInoperable(category)) return;

      gaPush({
        category: QUESTION_PANEL_CATEGORY,
        action: QUESTION_PANEL_ACTIONS.ACTIVATE_CATEGORY_SHORTCUT,
        label: category,
      });

      onSelectCategory(category);
    },
    [isCategoryInoperable, onSelectCategory],
  );

  /**
   * Translates the index the roving tab index activated into the category it stands for.
   * @param index - The position of the activated Category Shortcut in the shared order.
   */
  const handleActivate = useCallback(
    (index: number) => handleSelectCategory(CATEGORY_ORDER[index]),
    [handleSelectCategory],
  );

  const isItemDisabledForRoving = useCallback(
    (index: number) => isCategoryInoperable(CATEGORY_ORDER[index]),
    [isCategoryInoperable],
  );

  const { handleKeyDown, getTabIndex, setItemRef, focusItem } = useRovingTabIndex({
    itemCount: CATEGORY_ORDER.length,
    orientation,
    isItemDisabled: isItemDisabledForRoving,
    onActivate: handleActivate,
  });

  useEffect(() => {
    /* 
      INFO: The Shortcut Bar mounts when the panel closes, so focus restoration belongs here rather
      than in the closing component because the target does not exist yet at close time.
      shouldRestoreFocus is the one-shot request, focusCategory names the Category Shortcut the user
      came from, focusItem moves DOM focus and the roving active index together, and
      onFocusRestored clears the request so it cannot fire twice.
    */
    if (!shouldRestoreFocus) return;

    const restoreIndex = focusCategory ? CATEGORY_ORDER.indexOf(focusCategory) : 0;

    focusItem(restoreIndex === -1 ? 0 : restoreIndex);
    onFocusRestored();
  }, [shouldRestoreFocus, focusCategory, focusItem, onFocusRestored]);

  const toolbarLabel = intl.formatMessage({ id: EXAMPLE_QUESTION_PANEL_MESSAGES.shortcutBarToolbar });

  return (
    /* INFO: A draft makes the shortcuts inoperable through aria-disabled rather than inert. inert
    removes the subtree from assistive tech entirely, so a screen-reader user would lose the bar
    without ever being told why; aria-disabled keeps it announced, and the muted palette in
    CategoryControl.styles shows the same state visually at 4.5:1. */
    <ShortcutBarRoot data-testid={EXAMPLE_QUESTION_PANEL_TEST_IDS.shortcutBar}>
      <ShortcutBarLabel>
        <FormattedMessage id={EXAMPLE_QUESTION_PANEL_MESSAGES.shortcutBarIntro} />
      </ShortcutBarLabel>
      <CategoryRow role="toolbar" aria-label={toolbarLabel} aria-orientation={orientation} onKeyDown={handleKeyDown}>
        {CATEGORY_ORDER.map((category, index) => (
          <CategoryControl
            key={category}
            category={category}
            isInoperable={isCategoryInoperable(category)}
            tabIndex={getTabIndex(index)}
            testId={`${EXAMPLE_QUESTION_PANEL_TEST_IDS.categoryShortcutPrefix}${category}`}
            itemRef={setItemRef(index)}
            onSelect={handleSelectCategory}
          />
        ))}
      </CategoryRow>
    </ShortcutBarRoot>
  );
};

export default ExampleQuestionsShortcutBar;
